/**
 * Central map of route chunk loaders. Each entry is a bare `() => import(...)`
 * so the exact module used by `React.lazy` in `routes.tsx` is also the one a
 * hover-prefetch (IconRail / MobileNav) warms — the browser dedupes the second
 * `import()` to the already-in-flight or cached chunk.
 *
 * Pages use named exports, so `lazy()` wrappers below adapt them to the default
 * export shape `React.lazy` expects.
 */
import { prefetchHomeDashboardData } from "@/features/dashboard/home/homeLayoutCache";
import { prefetchFeedPageData } from "@/features/feed/feedPageCache";

export const routeModules = {
  dashboard: () => import("@/features/dashboard/home/HomePage"),
  bookings: () => import("@/features/bookings/BookingsPage"),
  bookingDetail: () => import("@/features/bookings/BookingDetailPage"),
  rates: () => import("@/features/rates/RatesPage"),
  rateCreate: () => import("@/features/rates/create/RateCreatePage"),
  rateCreateFromDocument: () =>
    import("@/features/rates/create/document/RateCreateFromDocumentPage"),
  rateDetail: () => import("@/features/rates/RateDetailPage"),
  schedule: () => import("@/features/schedule/SchedulePage"),
  tracking: () => import("@/features/tracking/TrackingPage"),
  reports: () => import("@/features/reports/ReportsPage"),
  reportDetail: () => import("@/features/reports/ReportDetailPage"),
  invoices: () => import("@/features/invoices/InvoicesPage"),
  invoiceBatch: () => import("@/features/invoices/upload/InvoiceBatchPage"),
  invoiceUploadLine: () =>
    import("@/features/invoices/upload/InvoiceUploadLinePage"),
  feed: () => import("@/features/feed/FeedPage"),
  preferences: () => import("@/features/preferences/PreferencesPage"),
  admin: () => import("@/features/admin/AdminPage"),
} as const;

export type RouteModuleKey = keyof typeof routeModules;

/**
 * Map an app nav path onto the route module keys worth prefetching for it.
 * Prefetching a list page also warms its detail chunk, since users usually
 * drill in right after the list renders.
 */
const NAV_PATH_PREFETCH: Record<string, RouteModuleKey[]> = {
  // "/" (Kai) is the eager landing page — no chunk to prefetch.
  "/dashboard": ["dashboard"],
  "/bookings": ["bookings", "bookingDetail"],
  "/rates": ["rates", "rateDetail"],
  "/rates/create": ["rateCreate"],
  "/rates/create/from-document": ["rateCreateFromDocument"],
  "/schedule": ["schedule"],
  "/tracking": ["tracking"],
  "/reports": ["reports", "reportDetail"],
  "/invoices": ["invoices", "invoiceBatch", "invoiceUploadLine"],
  "/feed": ["feed"],
  "/preferences": ["preferences"],
  "/admin": ["admin"],
};

const warmed = new Set<RouteModuleKey>();

/** Kick off a chunk download once; repeat calls for the same key are no-ops. */
export function prefetchRouteModule(key: RouteModuleKey): void {
  if (warmed.has(key)) return;
  warmed.add(key);
  void routeModules[key]().catch(() => {
    // A failed prefetch shouldn't stick — allow a later retry (e.g. real nav).
    warmed.delete(key);
  });
}

/** Prefetch every chunk associated with a nav path (used on rail hover). */
export function prefetchNavPath(path: string): void {
  const keys = NAV_PATH_PREFETCH[path];
  if (!keys) return;
  for (const key of keys) prefetchRouteModule(key);
  if (path === "/dashboard") prefetchHomeDashboardData();
  if (path === "/feed") prefetchFeedPageData();
}
